// hooks/useGeolocation.js
// BuzzInApp — Geolocalización con fallback y mock detection
//
// Expone la posición del usuario en tiempo real para:
//   - HomeScreen: ordenar comercios por distancia
//   - ClaimScreen: validar presencia y mostrar distancia en tiempo real
//
// Jerarquía: GPS (3-10m) → WiFi (15-40m) → Cell (100-300m)

import { useState, useEffect, useCallback, useRef } from "react";
import {
  getUserPosition,
  watchPosition,
  haversineDistance,
} from "../security/geoFence";
import { detectMockLocation } from "../security/fraudDetector";
import { GEOFENCING } from "../config/security";

// ─── Estado inicial ───────────────────────────────────────────────────────────
const INITIAL_STATE = {
  position:     null,   // GeolocationPosition raw
  coords:       null,   // { lat, lng, accuracy }
  tier:         null,   // "gps" | "wifi" | "cell" | "unavailable"
  mockDetected: false,
  mockFlags:    [],
  error:        null,
  loading:      true,
  permitted:    null,   // true | false | null (no preguntado aún)
};

// ─── Etiqueta legible del tier ────────────────────────────────────────────────
const TIER_LABELS = {
  gps:         "GPS",
  wifi:        "Wi-Fi",
  cell:        "Red móvil",
  unavailable: "Sin ubicación",
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useGeolocation = ({ watchMode = false, business = null } = {}) => {
  const [state, setState]   = useState(INITIAL_STATE);
  const previousPosRef      = useRef(null);
  const stopWatchRef        = useRef(null);

  // ─── Obtener posición única ────────────────────────────────────────────
  const fetchPosition = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));

    const { position, tier, error } = await getUserPosition({ highAccuracy: true });

    if (error || !position) {
      setState((s) => ({
        ...s,
        loading:   false,
        error,
        permitted: error === "location_denied" ? false : s.permitted,
        tier:      "unavailable",
      }));
      return null;
    }

    const mockResult = detectMockLocation(position, previousPosRef.current);
    previousPosRef.current = position;

    const coords = {
      lat:      position.coords.latitude,
      lng:      position.coords.longitude,
      accuracy: position.coords.accuracy,
    };

    setState({
      position,
      coords,
      tier,
      mockDetected: mockResult.isMock,
      mockFlags:    mockResult.flags,
      error:        null,
      loading:      false,
      permitted:    true,
    });

    return position;
  }, []);

  // ─── Modo watch continuo (ClaimScreen) ────────────────────────────────
  const startWatch = useCallback(() => {
    if (!business) return;
    if (stopWatchRef.current) stopWatchRef.current(); // cancelar watch anterior

    const stop = watchPosition((result) => {
      // result viene de validatePresence en geoFence.js
      setState((s) => ({
        ...s,
        tier:         result.tier,
        mockDetected: result.mockDetected,
        mockFlags:    result.mockFlags,
        error:        result.tier === "unavailable" ? result.reason : null,
        loading:      false,
        permitted:    result.tier !== "unavailable",
      }));
    }, business);

    stopWatchRef.current = stop;
  }, [business]);

  const stopWatch = useCallback(() => {
    if (stopWatchRef.current) {
      stopWatchRef.current();
      stopWatchRef.current = null;
    }
  }, []);

  // ─── Ciclo de vida ─────────────────────────────────────────────────────
  useEffect(() => {
    if (watchMode && business) {
      startWatch();
    } else {
      fetchPosition();
    }

    return stopWatch;
  }, [watchMode, business?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Validación de presencia para un comercio dado ─────────────────────
  /**
   * Calcula si el usuario está dentro del radio permitido del comercio.
   * Usa haversineDistance importado estáticamente desde geoFence.js.
   * @param {{ lat: number, lng: number }} biz
   * @returns {{ present: boolean, distanceMeters: number | null }}
   */
  const checkPresence = useCallback(
    (biz) => {
      if (!state.coords || !biz?.lat || !biz?.lng) {
        return { present: false, distanceMeters: null };
      }
      const distanceMeters = Math.round(
        haversineDistance(
          state.coords.lat,
          state.coords.lng,
          biz.lat,
          biz.lng
        )
      );
      return {
        present: distanceMeters <= GEOFENCING.MAX_DISTANCE_METERS,
        distanceMeters,
      };
    },
    [state.coords]
  );

  return {
    // Posición
    position:     state.position,
    coords:       state.coords,        // { lat, lng, accuracy } — para useBusinesses
    tier:         state.tier,
    tierLabel:    TIER_LABELS[state.tier] ?? "—",

    // Seguridad
    mockDetected: state.mockDetected,
    mockFlags:    state.mockFlags,

    // Estado
    loading:      state.loading,
    error:        state.error,
    permitted:    state.permitted,
    available:    state.tier !== "unavailable" && state.tier !== null,

    // Acciones
    refresh:      fetchPosition,
    startWatch,
    stopWatch,
    checkPresence,
  };
};

export default useGeolocation;
